import React, { useState } from 'react';
import { Play, Pause, X, Edit3, Check, Loader2, IndianRupee, Layers, Building2 } from 'lucide-react';
import { MarketplaceSubscription } from '../../types/marketplace';
import marketplaceService from '../../services/marketplaceService';
import { isWhiteLabel } from '../../config/tenantConfig';
import { CHIP_ACCENT, CHIP_DENSE, CHIP_RISK } from '../../utils/chipColors';
import { formatTradingType } from '../../utils/scheduleSummary';

interface SubscriptionCardProps {
  subscription: MarketplaceSubscription;
  onUpdate: () => void;
}

// Subscription status — `cls` carries combined bg+text; `stripColor` (hex)
// drives the card's top-edge strip and MUST visually agree with the chip on
// the same card (lines 94 + 99 in the JSX). PAUSED + CANCELLED route through
// CHIP_DENSE because their hex strips happen to share the canonical hue.
//
// ⚠️ ACTIVE intentionally stays inline emerald (NOT CHIP_DENSE.COMPLETE green):
// the top-edge strip is #10b981 (emerald-500) — a marketplace-specific hue
// also used by CHIP_CATEGORY.INCOME / CONSERVATIVE. Canonicalizing to green
// would split the strip / chip hues on the same card. If you change the strip
// hex, update the chip inline classes to match.
const statusConfig: Record<string, { label: string; cls: string; stripColor: string }> = {
  ACTIVE: {
    label: 'Active',
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    stripColor: '#10b981',
  },
  PAUSED:    { label: 'Paused',    cls: CHIP_DENSE.EXIT_PENDING, stripColor: '#f59e0b' },
  CANCELLED: { label: 'Cancelled', cls: CHIP_DENSE.CANCELLED,    stripColor: '#9ca3af' },
};

const riskBadgeColors: Record<string, string> = {
  LOW:    CHIP_RISK.LOW,
  MEDIUM: CHIP_RISK.MEDIUM,
  HIGH:   CHIP_RISK.HIGH,
};

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ subscription, onUpdate }) => {
  const wl = isWhiteLabel();
  const accentText = wl ? 'text-zebu-600 dark:text-zebu-400' : 'text-blue-600 dark:text-blue-400';
  const accentResume = wl ? 'bg-zebu-600 text-white hover:bg-zebu-700' : 'bg-blue-600 text-white hover:bg-blue-700';
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isEditingLots, setIsEditingLots] = useState(false);
  const [editLotValue, setEditLotValue] = useState(subscription.lot_multiplier);
  const [isSavingLots, setIsSavingLots] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = statusConfig[subscription.status] || statusConfig.CANCELLED;
  const capitalDeployed = (subscription.listing?.capital_required || 500000) * subscription.lot_multiplier;

  const handleToggleStatus = async () => {
    setIsTogglingStatus(true);
    setError(null);
    try {
      const newStatus = subscription.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      await marketplaceService.updateSubscription(subscription.subscription_id, { status: newStatus });
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleSaveLots = async () => {
    if (editLotValue === subscription.lot_multiplier) { setIsEditingLots(false); return; }
    setIsSavingLots(true);
    setError(null);
    try {
      await marketplaceService.updateSubscription(subscription.subscription_id, { lot_multiplier: editLotValue });
      setIsEditingLots(false);
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to update lots');
    } finally {
      setIsSavingLots(false);
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    setError(null);
    try {
      await marketplaceService.cancelSubscription(subscription.subscription_id);
      setShowCancelConfirm(false);
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to unsubscribe');
    } finally {
      setIsCancelling(false);
    }
  };

  const subscribedDate = subscription.subscribed_at
    ? new Date(subscription.subscribed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '-';

  return (
    <div
      className={`w-full text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 ${subscription.status === 'PAUSED' ? 'opacity-80' : ''}`}
      style={{ borderTopColor: status.stripColor, borderTopWidth: '4px' }}
    >
      <div className="p-5">
        {/* Status + Risk + Trading-type + Category badges.
            Trading-type chip uses the same `CHIP_ACCENT.TRADING_TYPE` cyan
            palette as the browse card + detail page for cross-surface
            consistency. Reads strategies_preview[0] from the subscription's
            listing sub-object (surfaced by list_user_subscriptions). */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${status.cls}`}>
            {status.label}
          </span>
          {subscription.listing?.risk_level && (
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${riskBadgeColors[subscription.listing.risk_level] || riskBadgeColors.MEDIUM}`}>
              {subscription.listing.risk_level}
            </span>
          )}
          {(() => {
            const primary = subscription.listing?.strategies_preview?.[0];
            if (!primary?.trading_type) return null;
            const label = formatTradingType(
              primary.trading_type,
              primary.intraday_exit_mode,
              primary.entry_trading_days_before_expiry,
              primary.exit_trading_days_before_expiry,
            );
            if (!label) return null;
            return (
              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${CHIP_ACCENT.TRADING_TYPE}`}>
                {label}
              </span>
            );
          })()}
          {subscription.listing?.category && (
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {subscription.listing.category}
            </span>
          )}
        </div>

        {/* Title + subscribed date */}
        <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight mb-0.5">
          {subscription.listing?.title || 'Marketplace Basket'}
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
          Subscribed {subscribedDate}
        </p>

        {/* Capital deployed — prominent figure */}
        <div className="mb-3">
          <p className="text-[0.65rem] text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-0.5">Capital Deployed</p>
          <p className={`text-2xl font-black ${accentText}`}>
            ₹{capitalDeployed.toLocaleString('en-IN')}
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 border-t border-gray-100 dark:border-gray-700 pt-3 mb-3">
          {/* Lot multiplier — editable */}
          <div>
            <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase flex items-center gap-0.5">
              <Layers className="h-2.5 w-2.5" /> Lots
            </p>
            {isEditingLots ? (
              <span className="inline-flex items-center gap-1 mt-0.5">
                <input
                  type="number"
                  value={editLotValue}
                  onChange={(e) => setEditLotValue(parseInt(e.target.value) || 1)}
                  min={1}
                  className="w-10 h-5 px-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={isSavingLots}
                />
                <button onClick={handleSaveLots} disabled={isSavingLots} className={`${accentText} disabled:opacity-50`}>
                  {isSavingLots ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                </button>
                <button onClick={() => { setIsEditingLots(false); setEditLotValue(subscription.lot_multiplier); }} className="text-gray-400">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : (
              <p className="text-xs font-semibold text-gray-900 dark:text-white flex items-center gap-1 mt-0.5">
                {subscription.lot_multiplier}x
                {subscription.status !== 'CANCELLED' && (
                  <button onClick={() => setIsEditingLots(true)} className={`text-gray-400 ${wl ? 'hover:text-zebu-600' : 'hover:text-blue-600'} transition-colors`}>
                    <Edit3 className="h-2.5 w-2.5" />
                  </button>
                )}
              </p>
            )}
          </div>

          <div>
            <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase flex items-center gap-0.5">
              <IndianRupee className="h-2.5 w-2.5" /> Per Lot
            </p>
            <p className="text-xs font-semibold text-gray-900 dark:text-white mt-0.5">
              ₹{(subscription.listing?.capital_required || 500000).toLocaleString('en-IN')}
            </p>
          </div>

          <div>
            <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase flex items-center gap-0.5">
              <Building2 className="h-2.5 w-2.5" /> Account
            </p>
            <p className="text-xs font-semibold text-gray-900 dark:text-white mt-0.5 truncate">
              {subscription.zebu_client_id}
            </p>
          </div>
        </div>

        {/* Error */}
        {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}

        {/* Cancel confirmation */}
        {showCancelConfirm ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-xs text-red-800 dark:text-red-200 mb-2">Unsubscribe? This stops all automated trades.</p>
            <div className="flex gap-2">
              <button onClick={handleCancel} disabled={isCancelling} className="px-3 py-1 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50">
                {isCancelling ? 'Unsubscribing...' : 'Confirm'}
              </button>
              <button onClick={() => setShowCancelConfirm(false)} disabled={isCancelling} className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900">
                Keep
              </button>
            </div>
          </div>
        ) : subscription.status !== 'CANCELLED' && (
          /* Footer actions */
          <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-700 pt-3">
            <button
              onClick={() => setShowCancelConfirm(true)}
              className="text-xs font-medium text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
            >
              Unsubscribe
            </button>
            <button
              onClick={handleToggleStatus}
              disabled={isTogglingStatus}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 ${
                subscription.status === 'ACTIVE'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                  : accentResume
              }`}
            >
              {isTogglingStatus ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : subscription.status === 'ACTIVE' ? (
                <><Pause className="h-3.5 w-3.5" /> Pause</>
              ) : (
                <><Play className="h-3.5 w-3.5" /> Resume</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCard;
